import { memo, useState } from 'react';
import { useDateNow, useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import { getBlockTranslationProgress } from 'shared-helpers/courses';
import { breakpoints } from 'styles';
import { getFormattedDate } from 'lib/common';
import { getCourseLanguageVersionOptions } from 'lib/courses';
import { Button, Tooltip } from 'components/common';
import { Icon } from 'components/common/icons';
import { editorSocket } from 'components/editor/socket';
import { EditorBlockAudio } from './audio';
import { EditorBlockDiscussion } from './discussion';
import { EditorBlockEmbedCode, EditorBlockEmbedLink } from './embed';
import { EditorBlockFile } from './file';
import { EditorBlockFillIn } from './fillIn';
import { EditorBlockH5P } from './h5p';
import { EditorBlockHeading } from './heading';
import { EditorBlockImage } from './image';
import { EditorBlockList } from './list';
import { EditorBlockLikert } from './likert';
import { EditorBlockMath } from './math';
import { EditorBlockMultipleChoice } from './multipleChoice';
import { EditorBlockNumericQuestion } from './numericQuestion';
import { EditorBlockOpenQuestion } from './openQuestion';
import { EditorBlockPageBreak } from './pageBreak';
import { EditorBlockPre } from './pre';
import { EditorBlockQuote } from './quote';
import { EditorBlockRaw } from './raw';
import { EditorBlockScorm } from './scorm';
import { EditorBlockSequencing } from './sequencing';
import { EditorBlockSingleChoice } from './singleChoice';
import { EditorBlockTable } from './table';
import { EditorBlockText } from './text';
import { EditorBlockVideo } from './video';
import {
  EditorBlockControls,
  EditorBlockErrorBoundary,
  EditorBlockGenerateTranslations,
  EditorBlockInfo,
  EditorBlockIsSelectedOptions
} from '.';

import type { Block, Course, ServerConfig } from 'shared-types';
import type { Editor, EditorBlockTypes } from 'types';

const blockTypes: Record<EditorBlockTypes, React.ElementType> = {
  audio: EditorBlockAudio,
  discussion: EditorBlockDiscussion,
  embedcode: EditorBlockEmbedCode,
  embedlink: EditorBlockEmbedLink,
  file: EditorBlockFile,
  fillin: EditorBlockFillIn,
  h5p: EditorBlockH5P,
  heading: EditorBlockHeading,
  image: EditorBlockImage,
  likert: EditorBlockLikert,
  list: EditorBlockList,
  mathjax: EditorBlockMath,
  multiplechoice: EditorBlockMultipleChoice,
  numericquestion: EditorBlockNumericQuestion,
  openquestion: EditorBlockOpenQuestion,
  pagebreak: EditorBlockPageBreak,
  pre: EditorBlockPre,
  quote: EditorBlockQuote,
  raw: EditorBlockRaw,
  scorm: EditorBlockScorm,
  sequencing: EditorBlockSequencing,
  singlechoice: EditorBlockSingleChoice,
  table: EditorBlockTable,
  text: EditorBlockText,
  video: EditorBlockVideo
};

export interface BlockProps<T = any> {
  blockId: number;
  data: T;
  isAIPreview?: boolean;
  isPreview?: boolean;
  languageVersion: Course['language'];
  serverConfig: ServerConfig<'guest'>;
  type: string;
  updateData: (data: T) => void;
}

export interface EditorStateProps {
  editorChapter: Editor['editorCurrentChapter'];
  editorBlockIsMoving: Editor['editorIsBlockMoveView'];
  editorBlockIdForCropping: Editor['editorCroppingBlockId'];
  editorLanguageActive: Editor['editorActiveLanguageVersion'];
  editorLanguageReference: Editor['editorTranslationReferenceLanguage'];
  editorPreviewType: Editor['editorPreviewType'];
}

interface Props extends EditorStateProps {
  blockId: number;
  chapterId: number;
  data: any;
  editor: any;
  isEdited: boolean;
  isNew: boolean;
  isSelected: boolean;
  languagesMissingTranslations: Course['language'][];
  languageVersion: Course['language'];
  type: Block['type'];
  updatedAt: string;
}

const EditorBlock = ({
  blockId,
  chapterId,
  data,
  editor,
  isEdited,
  isNew,
  isSelected,
  languagesMissingTranslations,
  languageVersion,
  type,
  updatedAt,
  ...editorStateProps
}: Props) => {
  const { editorLanguageActive, editorPreviewType } = editorStateProps;

  const { dateNow } = useDateNow();

  const {
    messages,
    pageState: { course },
    serverConfig,
    theme
  } = useReduxState();

  const [isError, setIsError] = useState(false);
  const [isDeleteView, setIsDeleteView] = useState(false);

  const handleDelete = () => {
    editorSocket.deleteBlocks({ blockIds: [blockId], chapterId });
  };

  const handleError = () => setIsError(true);

  const handleSelectedClick = () => {
    editorSocket.selectBlock({ blockId, isSelected: !isSelected });
  };

  const handleVisibilityClick = () => {
    const newData = { ...data };
    newData.visibility =
      newData?.visibility === 'hidden' ? 'visible' : 'hidden';
    updateData(newData);
  };

  const toggleDeleteView = () => setIsDeleteView(!isDeleteView);

  const toggleMoveView = () => editorSocket.setBlockBeingMoved({ blockId });

  // set timeout to update data
  const updateData = (data: Props['data']) => {
    setIsError(false);
    editorSocket.updateBlock({ blockId, data });
  };

  const displayEditorBlockControls =
    !editorLanguageActive && !editorPreviewType;

  // single choice blocks don't have their own type in the db
  const isSingleChoice = type === 'multiplechoice' && data.isSingleChoice;

  const BlockType = blockTypes[isSingleChoice ? 'singlechoice' : type];

  const markedAsNew =
    data?.markAsNewUntil && Date.parse(data.markAsNewUntil) > dateNow;

  const markAsNewUntilTooltip =
    markedAsNew &&
    getMessageWithPlaceholders({
      message: messages.editorMarkedAsNew,
      placeholders: { date: getFormattedDate({ date: data.markAsNewUntil }) }
    });

  const languageOptions = getCourseLanguageVersionOptions({
    languageKeys: languagesMissingTranslations
  });

  const otherTranslationsMissing = editorLanguageActive
    ? []
    : languagesMissingTranslations;

  const otherTranslationsMissingTooltip =
    messages.courseLanguageVersionsMissingTranslations +
    (editorLanguageActive && otherTranslationsMissing?.length
      ? ''
      : `: ${otherTranslationsMissing
          ?.map((language) => {
            const languageOption = languageOptions[language];
            return languageOption?.capitalizedName || language;
          })
          .join(', ')}`);

  const currentVersionTranslationsMissing =
    editorLanguageActive && !editorPreviewType
      ? getBlockTranslationProgress({
          block: { data, type },
          courseDefaultLanguage: course.language
        })[editorLanguageActive] < 100
      : false;

  const innerBottomMargin = Boolean(editorLanguageActive) ? 40 : 0;
  const innerBottomPadding =
    editorLanguageActive && !['image'].includes(type) ? 10 : 50;

  return (
    <EditorBlockErrorBoundary onError={handleError}>
      <div
        className={
          `block block-${type}` +
          (isError ? ' block-error' : '') +
          (isDeleteView ? ' block-delete' : '') +
          (isNew ? ' block-new' : '') +
          (!isNew && isEdited ? ' block-edited' : '') +
          (isSelected ? ' block-selected' : '') +
          (currentVersionTranslationsMissing
            ? ' block-current-translations-missing'
            : '')
        }
        data-block-id={blockId}
      >
        <div className="block-before">
          {isNew ? (
            <Tooltip childrenWrapInDiv={false} content={messages.commonNew}>
              <div className="status-icon status-icon-success">
                <Icon icon="check" />
              </div>
            </Tooltip>
          ) : isEdited ? (
            <Tooltip childrenWrapInDiv={false} content={messages.commonEdited}>
              <div className="status-icon status-icon-warning">
                <Icon icon="pen" />
              </div>
            </Tooltip>
          ) : null}

          {markedAsNew ? (
            <Tooltip childrenWrapInDiv={false} content={markAsNewUntilTooltip}>
              <div className="status-icon">
                <Icon icon="thumbtack" />
              </div>
            </Tooltip>
          ) : null}

          {!isNew && otherTranslationsMissing?.length ? (
            <Tooltip
              childrenWrapInDiv={false}
              content={otherTranslationsMissingTooltip}
            >
              <div className="status-icon status-icon-warning-alt">
                <Icon icon="exclamation" />
              </div>
            </Tooltip>
          ) : !isNew && currentVersionTranslationsMissing ? (
            <Tooltip
              childrenWrapInDiv={false}
              content={messages.courseLanguageVersionsMissingTranslations}
            >
              <div className="status-icon status-icon-warning-alt">
                <Icon icon="exclamation" />
              </div>
            </Tooltip>
          ) : null}
        </div>

        <div className="inner">
          <div
            className={`status ${
              isNew ? 'status-new' : isEdited ? 'status-edited' : ''
            }`}
          />

          {BlockType ? (
            <EditorBlockInfo editor={editor} updatedAt={updatedAt} />
          ) : null}

          {isError ? (
            <div style={{ textAlign: 'center' }}>
              <div>{messages.editorBlockError}</div>
              <div>{`(${type})`}</div>
            </div>
          ) : BlockType ? (
            <div className="block-content">
              <BlockType
                blockId={blockId}
                data={data}
                isPreview={!!editorPreviewType}
                languageVersion={languageVersion}
                serverConfig={serverConfig}
                type={type}
                updateData={updateData}
                {...editorStateProps}
              />
            </div>
          ) : (
            <div>{type}</div>
          )}

          {displayEditorBlockControls && (
            <EditorBlockControls
              blockData={data}
              blockId={blockId}
              blockType={isSingleChoice ? 'singlechoice' : type}
              isDeleteView={isDeleteView}
              isSelected={isSelected}
              isVisible={data?.visibility !== 'hidden'}
              onDelete={handleDelete}
              onVisibilityClick={handleVisibilityClick}
              toggleDeleteView={toggleDeleteView}
              toggleMoveView={toggleMoveView}
              {...editorStateProps}
            />
          )}

          <EditorBlockGenerateTranslations
            blockData={data}
            blockId={blockId}
            updateData={updateData}
          />

          {data?.visibility === 'hidden' ? (
            <div className="hidden-overlay">
              <Icon
                icon="eye-slash"
                style={{
                  color: theme.textColorLight,
                  fontSize: '1.6rem',
                  position: 'absolute',
                  top: 15,
                  right: 15
                }}
              />
            </div>
          ) : null}
        </div>

        {!editorPreviewType && !editorLanguageActive && (
          <div
            className={`block-select-button ${isSelected ? 'selected' : ''}`}
          >
            <Button
              aria-checked={isSelected}
              aria-label={messages.editorBlockControlsSelect}
              category={isSelected ? 'success' : 'lighter'}
              data-tooltip={messages.editorBlockControlsSelect}
              icon="check"
              iconStyle={{ color: 'inherit' }}
              onClick={handleSelectedClick}
              role="checkbox"
              size="small"
              style={{ borderRadius: 5 }}
            />
          </div>
        )}

        {!editorPreviewType && !editorLanguageActive && isSelected ? (
          <div className="block-select-all">
            <EditorBlockIsSelectedOptions
              blockId={blockId}
              data={data}
              markedAsNew={markedAsNew}
              messages={messages}
              type={type}
            />
          </div>
        ) : null}

        <style jsx>{`
          .block {
            margin: 10px 0;
            padding: 0 40px;
            position: relative;
          }
          .block-before {
            display: flex;
            flex-direction: column;
            gap: 5px;
            position: absolute;
            top: 0px;
            left: 2px;
            width: 100%;
            height: 100%;
          }
          .status-icon {
            align-items: center;
            background-color: ${theme.mainColor};
            border-radius: 20px;
            color: ${theme.mainTextColor};
            display: inline-flex;
            font-size: 0.9rem;
            justify-content: center;
            width: 20px;
            height: 20px;
          }
          .status-icon-success {
            background-color: ${theme.successLightColor};
            color: ${theme.successLightTextColor};
          }
          .status-icon-warning {
            background-color: ${theme.warningLightColor};
            color: ${theme.warningLightTextColor};
          }
          .status-icon-warning-alt {
            background-color: ${theme.warningStandaloneColor};
            color: white;
          }
          .inner {
            border: 2px dashed transparent;
            border-radius: ${theme.cardBorderRadius};
            padding: 10px 10px;
            position: relative;
            padding-bottom: ${innerBottomPadding + 38}px;
            margin-bottom: ${innerBottomMargin}px;
          }
          .block:hover .inner,
          .block:focus-within .inner {
            border-color: ${theme.lightGreyDarkActive};
          }
          .block-selected .inner,
          .block-selected:hover .inner,
          .block-selected:focus-within .inner {
            border: 2px solid ${theme.successColor};
          }
          .block-delete .inner,
          .block-delete:hover .inner,
          .block-delete:focus-within .inner,
          .block-error .inner,
          .block-error:hover .inner,
          .block-error:focus-within .inner {
            border: 2px solid ${theme.dangerColor};
          }
          .block-current-translations-missing .inner,
          .block-current-translations-missing:hover .inner,
          .block-current-translations-missing:focus-within .inner {
            border-color: ${theme.warningStandaloneColor};
          }
          .status {
            background-color: transparent;
            border-radius: 2px;
            content: '';
            position: absolute;
            top: 0;
            bottom: 40px;
            left: -10px;
            width: 4px;
          }
          .status.status-edited {
            background-color: ${theme.warningLightColor};
          }
          .status.status-new {
            background-color: ${theme.successLightColor};
          }
          .hidden-overlay {
            background: rgba(247, 227, 224, 0.5);
            border-radius: 3px;
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
          }
          .block-select-button {
            position: absolute;
            right: 5px;
            top: 1px;
            visibility: hidden;
          }
          .block-select-button :global(button) {
            color: transparent;
            transition: color 0.3s;
          }
          .block-select-button :global(button:hover) {
            color: #b1b8bf;
          }
          .block-select-button.selected :global(button) {
            color: ${theme.successTextColor};
          }
          .block-select-all {
            position: absolute;
            right: 5px;
            top: 36px;
          }
          .block :global(a) {
            cursor: pointer;
          }
          .block :global(code) {
            font-family: 'Lucida Console', 'Monaco', monospace;
          }
          .block :global(span > code) {
            background-color: ${theme.lightGrey};
            border-radius: 2px;
            font-size: 13px;
            padding: 2px 4px;
          }
          .block:hover .block-select-button,
          .block:hover :global(.editor-block-controls),
          .block:hover :global(.editor-block-custom-controls),
          .block:focus-within .block-select-button,
          .block:focus-within :global(.editor-block-controls),
          .block:focus-within :global(.editor-block-custom-controls),
          .block-selected :global(.editor-block-controls) {
            visibility: visible;
          }
          .block:hover :global(.block-generate-translations),
          .block:focus-within :global(.block-generate-translations) {
            visibility: visible;
          }
          @media (min-width: ${breakpoints.medium}px) {
            .inner {
              padding-bottom: ${innerBottomPadding}px;
            }
          }
        `}</style>
      </div>
    </EditorBlockErrorBoundary>
  );
};

export default memo(EditorBlock);
